<script>
import { mapGetters } from 'vuex';
import AntInput from '@/components/AntInput.vue';
import { executeCustomModuleCall } from '@/services/api/module.api';
import {
  DHME_DRAWING_BOARD,
  DHME_M_ASSEMBLY_HALL_DASHBOARD,
} from '@/modules/modules';
import { getRecordDocument } from '@/services/api/record.api';
import { queryTablesV2 } from '@/services/api/v2/connect_v2';
import AutomatedTrestleOverview from '@/modules/daiwa-house-modular-europe/AssemblyHallDashboard/DhmeAssemblyHallDashboardAutomatedTrestles.vue';
import ManualTrestleOverview from '@/modules/daiwa-house-modular-europe/AssemblyHallDashboard/DhmeAssemblyHallDashboardManualTrestles.vue';

export default {
  name: 'DrawingBoard',
  components: { AntInput, AutomatedTrestleOverview, ManualTrestleOverview },
  data: () => ({
    halls: [],
    hallProjects: [],
    focusedHall: null,
    focusedProject: null,
    drawingTab: 'overview',

    projectDrawings: [],
    projectDrawingsTableId: null,
    projectModules: [],
    projectModulesTableId: null,

    displayedDrawing: null,
    focusedDrawingId: null,
    imageLoading: false,

    hallAssemblyTasks: [],
    assemblyTasksLoading: false,

    moduleStatusFilter: [],
    moduleTypeFilter: [],
    moduleSortBy: 'assembly_sequence',
    moduleSortDesc: false,
    moduleSortOptions: [
      { text: 'Assembly order', value: 'assembly_sequence' },
      { text: 'Module ID', value: 'module_id' },
      { text: 'Build nr', value: 'build_nr' },
      { text: 'Type', value: 'module_type' },
      { text: 'Status', value: 'status' },
    ],
  }),
  computed: {
    ...mapGetters(['project']),
    moduleId() {
      return (
        this.project.modules.find(
          (module) => module.route === DHME_DRAWING_BOARD
        )?.id ?? null
      );
    },
    assemblyHallModuleId() {
      return (
        this.project.modules.find(
          (module) => module.route === DHME_M_ASSEMBLY_HALL_DASHBOARD
        )?.id ?? null
      );
    },
    hasTrestleSupport() {
      return !!this.assemblyHallModuleId;
    },
    hallLocations() {
      return this.$store.getters[
        'dhmeAssemblyHallDashboardStore/hallLocations'
      ];
    },
    moduleStatusMap() {
      const map = {};
      for (const task of this.hallAssemblyTasks) {
        const mid = task.assemblyItem?.id;
        if (!mid) continue;

        let status = 'todo';
        let locationName = null;

        if (task.status === 'closed') {
          status = 'completed';
        } else if (task.task_type?.custom_11) {
          status = 'on_location';
          const loc = this.hallLocations.find(
            (l) => l.id === task.task_type.custom_11
          );
          locationName = loc ? loc.assembly_location : null;
        } else if (task.task_type?.custom_3) {
          status = 'on_location';
          locationName = task.task_type.custom_3;
        }

        map[mid] = { status, locationName };
      }
      return map;
    },
    sortedDrawings() {
      return [...this.projectDrawings].sort((a, b) =>
        String(a.title ?? '').localeCompare(String(b.title ?? ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );
    },
    moduleTypes() {
      const types = new Set(
        this.projectModules.map((m) => m.module_type).filter(Boolean)
      );
      return [...types].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true })
      );
    },
    moduleStatusCounts() {
      const counts = { todo: 0, on_location: 0, completed: 0 };
      for (const module of this.projectModules) {
        const status = this.moduleStatusMap[module.module_id]?.status ?? 'todo';
        counts[status]++;
      }
      return counts;
    },
    filteredModules() {
      let modules = [...this.projectModules];

      if (this.moduleStatusFilter.length) {
        modules = modules.filter((m) =>
          this.moduleStatusFilter.includes(
            this.moduleStatusMap[m.module_id]?.status ?? 'todo'
          )
        );
      }

      if (this.moduleTypeFilter.length) {
        modules = modules.filter((m) =>
          this.moduleTypeFilter.includes(m.module_type)
        );
      }

      const statusOrder = { on_location: 0, todo: 1, completed: 2 };
      const key = this.moduleSortBy;
      modules.sort((a, b) => {
        let result;
        if (key === 'status') {
          result =
            statusOrder[this.moduleStatusMap[a.module_id]?.status ?? 'todo'] -
            statusOrder[this.moduleStatusMap[b.module_id]?.status ?? 'todo'];
        } else {
          const av = a[key];
          const bv = b[key];
          const an = parseFloat(av);
          const bn = parseFloat(bv);
          if (!isNaN(an) && !isNaN(bn)) {
            result = an - bn;
          } else {
            result = String(av ?? '').localeCompare(
              String(bv ?? ''),
              undefined,
              { numeric: true }
            );
          }
        }
        return this.moduleSortDesc ? -result : result;
      });

      return modules;
    },
    hasModuleFilters() {
      return !!(this.moduleStatusFilter.length || this.moduleTypeFilter.length);
    },
  },
  watch: {
    focusedHall(value) {
      if (value) {
        this.fetchProjects();
        if (this.hasTrestleSupport) {
          this.$store.dispatch(
            'dhmeAssemblyHallDashboardStore/selectAssemblyHall',
            value
          );
        }
        const currentQuery = { ...this.$route.query, hall: value.id };
        this.$router.push({ query: currentQuery });
      } else {
        this.focusedProject = null;
        this.hallProjects = null;
        this.hallAssemblyTasks = [];
        const currentQuery = { ...this.$route.query };
        delete currentQuery.hall;
        this.$router.push({ query: currentQuery });
      }
    },

    focusedProject(value) {
      this.clearModuleFilters();
      if (value) {
        this.fetchProjectDrawings();
        this.fetchAssemblyTasks();
        const currentQuery = { ...this.$route.query, project: value.id };
        this.$router.push({ query: currentQuery });
      } else {
        this.hallAssemblyTasks = [];
        const currentQuery = { ...this.$route.query };
        delete currentQuery.project;
        this.$router.push({ query: currentQuery });
      }
    },
  },
  mounted() {
    this.fetchHalls();
    if (this.assemblyHallModuleId) {
      this.$store.commit(
        'dhmeAssemblyHallDashboardStore/set_module_id',
        this.assemblyHallModuleId
      );
    }
  },
  methods: {
    async fetchProjectDrawings() {
      let { drawings, modules } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_DRAWINGS',
            project: this.focusedProject.id,
            as: 'drawings',
            columns: [{ name: 'title' }, { name: 'drawing' }],
          },
          {
            name: 'CFFA_DHME_MODULES',
            project: this.focusedProject.id,
            as: 'modules',
            sortBy: 'assembly_sequence',
            columns: [
              { name: 'module_id' },
              { name: 'module_type' },
              { name: 'build_nr' },
              { name: 'assembly_sequence' },
              { name: '2d_drawing' },
            ],
          },
        ],
      });

      this.projectDrawings = drawings.records;
      this.projectDrawingsTableId = drawings.id;
      this.projectModules = modules.records;
      this.projectModulesTableId = modules.id;
    },

    async fetchHalls() {
      let { halls } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_ASSEMBLY_HALLS',
            project: this.project.id,
            as: 'halls',
            columns: [
              { name: 'hall', as: 'title' },
              { name: 'automated' },
              {
                name: 'type',
                conditions: [{ operator: '=', value: 'assembly' }],
              },
            ],
          },
        ],
      });

      this.halls = halls.records;

      if (this.$route.query.hall) {
        this.focusedHall = this.halls.find(
          (x) => x.id === this.$route.query.hall
        );
      }
    },

    async fetchProjects() {
      this.hallProjects = await executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'fetchHallProjects',
        { hall: this.focusedHall.id }
      );

      if (this.$route.query.project) {
        this.focusedProject = this.hallProjects.find(
          (x) => x.id === this.$route.query.project
        );
      }
    },

    async fetchAssemblyTasks() {
      if (
        !this.focusedHall ||
        !this.focusedProject ||
        !this.assemblyHallModuleId
      )
        return;
      this.assemblyTasksLoading = true;
      try {
        this.hallAssemblyTasks = await executeCustomModuleCall(
          this.project.id,
          this.assemblyHallModuleId,
          'fetchHallAssemblyTasks',
          {
            hall: this.focusedHall.id,
            project: this.focusedProject.id,
          }
        );
      } catch (e) {
        this.hallAssemblyTasks = [];
      } finally {
        this.assemblyTasksLoading = false;
      }
    },

    async displayDrawing(table, recordId, item) {
      this.imageLoading = true;
      this.displayedDrawing = null;
      if (item) {
        try {
          const file = await getRecordDocument(
            item.id,
            this.focusedProject.id,
            table
          );
          this.displayedDrawing =
            URL.createObjectURL(this.b64toBlob(file.file, file.mimetype)) +
            '#navpanes=0&scrollbar=0&view=Fit';
        } catch (e) {}
      }
      this.imageLoading = false;
    },

    b64toBlob(b64Data, contentType) {
      const byteCharacters = atob(b64Data);
      let byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        let slice = byteCharacters.slice(offset, offset + 512),
          byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }
      return new Blob(byteArrays, { type: contentType });
    },

    clearModuleFilters() {
      this.moduleStatusFilter = [];
      this.moduleTypeFilter = [];
      this.moduleSortBy = 'assembly_sequence';
      this.moduleSortDesc = false;
    },

    setModuleSort(key) {
      if (this.moduleSortBy === key) {
        this.moduleSortDesc = !this.moduleSortDesc;
      } else {
        this.moduleSortBy = key;
        this.moduleSortDesc = false;
      }
    },

    toggleModuleType(type) {
      const index = this.moduleTypeFilter.indexOf(type);
      if (index === -1) {
        this.moduleTypeFilter.push(type);
      } else {
        this.moduleTypeFilter.splice(index, 1);
      }
    },

    moduleStatusColor(status) {
      if (status === 'completed') return 'success';
      if (status === 'on_location') return 'primary';
      return 'grey lighten-1';
    },

    moduleStatusIcon(status) {
      if (status === 'completed') return 'mdi-check-circle';
      if (status === 'on_location') return 'mdi-map-marker';
      return 'mdi-clock-outline';
    },

    moduleStatusLabel(status, locationName) {
      if (status === 'completed') return 'Done';
      if (status === 'on_location')
        return locationName ? `Loc. ${locationName}` : 'On location';
      return 'Todo';
    },
  },
};
</script>

<template>
  <div class="d-flex full-height overflow-hidden">
    <!-- Left panel: navigation -->
    <div
      class="background-white ant-border-right d-flex flex-column"
      style="width: 400px; min-width: 400px"
    >
      <div class="py-2 px-5">
        <ant-input label="Hall" style="margin-top: 0 !important">
          <template #input-field>
            <v-select
              v-model="focusedHall"
              :items="halls"
              dense
              filled
              hide-details
              item-text="title"
              item-value="id"
              placeholder="Hall"
              return-object
            >
              <template #item="{ item }">
                {{ item.title }}
                <v-spacer />
                <v-chip class="mr-2" small>
                  <v-icon left small>{{
                    item.automated
                      ? 'mdi-robot-industrial'
                      : 'mdi-account-wrench'
                  }}</v-icon>
                  {{ item.automated ? 'Automated' : 'Manual' }}
                </v-chip>
              </template>
            </v-select>
          </template>
        </ant-input>
        <ant-input label="Project">
          <template #input-field>
            <v-select
              v-model="focusedProject"
              :disabled="!focusedHall"
              :items="hallProjects"
              dense
              filled
              hide-details
              item-text="name"
              item-value="id"
              placeholder="Project"
              return-object
            />
          </template>
        </ant-input>
      </div>

      <div
        v-if="focusedProject"
        class="d-flex flex-column flex-1 flex-scroll-height"
      >
        <v-btn-toggle
          v-model="drawingTab"
          class="full-width"
          color="primary"
          group
          mandatory
          tile
        >
          <v-btn class="flex-grow-1" value="overview">Overview</v-btn>
          <v-btn class="flex-grow-1" value="modules">Modules</v-btn>
          <v-btn v-if="hasTrestleSupport" class="flex-grow-1" value="trestles">
            Trestles
          </v-btn>
        </v-btn-toggle>

        <!-- Module filter / sort toolbar -->
        <div
          v-if="drawingTab === 'modules'"
          class="px-5 py-1 ant-border-bottom d-flex align-center"
        >
          <!-- Status filter chips (icon + count) -->
          <v-chip-group
            v-if="hasTrestleSupport"
            v-model="moduleStatusFilter"
            multiple
          >
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-chip outlined small value="todo" v-on="on">
                  <v-icon left small>mdi-clock-outline</v-icon>
                  {{ moduleStatusCounts.todo }}
                </v-chip>
              </template>
              <span>Todo</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-chip
                  color="primary"
                  outlined
                  small
                  value="on_location"
                  v-on="on"
                >
                  <v-icon left small>mdi-map-marker</v-icon>
                  {{ moduleStatusCounts.on_location }}
                </v-chip>
              </template>
              <span>On location</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-chip
                  color="success"
                  outlined
                  small
                  value="completed"
                  v-on="on"
                >
                  <v-icon left small>mdi-check-circle</v-icon>
                  {{ moduleStatusCounts.completed }}
                </v-chip>
              </template>
              <span>Done</span>
            </v-tooltip>
          </v-chip-group>

          <v-spacer />

          <v-btn v-if="hasModuleFilters" icon small @click="clearModuleFilters">
            <v-icon small>mdi-filter-off-outline</v-icon>
          </v-btn>

          <!-- Type filter menu -->
          <v-menu
            v-if="moduleTypes.length"
            :close-on-content-click="false"
            offset-y
          >
            <template #activator="{ on, attrs }">
              <v-btn
                :color="moduleTypeFilter.length ? 'primary' : undefined"
                icon
                small
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi-shape-outline</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="type in moduleTypes"
                :key="type"
                @click="toggleModuleType(type)"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon
                    :color="
                      moduleTypeFilter.includes(type) ? 'primary' : undefined
                    "
                    small
                  >
                    {{
                      moduleTypeFilter.includes(type)
                        ? 'mdi-checkbox-marked'
                        : 'mdi-checkbox-blank-outline'
                    }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ type }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Sort menu -->
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon small>
                  {{
                    moduleSortDesc
                      ? 'mdi-sort-descending'
                      : 'mdi-sort-ascending'
                  }}
                </v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="option in moduleSortOptions"
                :key="option.value"
                @click="setModuleSort(option.value)"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon
                    v-if="moduleSortBy === option.value"
                    color="primary"
                    small
                  >
                    {{ moduleSortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title
                  :class="{ 'primary--text': moduleSortBy === option.value }"
                >
                  {{ option.text }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <!-- Drawing / module list (hidden when trestles tab is active) -->
        <v-list
          v-if="drawingTab !== 'trestles'"
          class="flex-grow-1 py-2 px-5 overflow-y-auto"
          nav
        >
          <v-list-item-group
            v-if="drawingTab === 'overview'"
            v-model="focusedDrawingId"
            color="primary"
          >
            <div
              v-if="!projectDrawings.length"
              class="d-flex flex-column align-center text--secondary py-8"
            >
              <v-icon class="mb-2" color="grey lighten-1" large>
                mdi-file-document-outline
              </v-icon>
              <span>No drawings found</span>
            </div>
            <v-card
              v-for="item in sortedDrawings"
              :key="item.id"
              :class="{ focused: focusedDrawingId === item.id }"
              class="mb-2"
              outlined
            >
              <v-list-item
                :value="item.id"
                class="px-4"
                color="primary"
                @click="
                  displayDrawing(projectDrawingsTableId, item.id, item.drawing)
                "
              >
                <v-list-item-icon class="my-auto mr-3">
                  <v-icon :color="item.drawing ? 'primary' : 'grey lighten-1'">
                    {{
                      item.drawing
                        ? 'mdi-file-document-outline'
                        : 'mdi-file-remove-outline'
                    }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-card>
          </v-list-item-group>

          <v-list-item-group
            v-if="drawingTab === 'modules'"
            v-model="focusedDrawingId"
            color="primary"
          >
            <div
              v-if="!filteredModules.length"
              class="d-flex flex-column align-center text--secondary py-8"
            >
              <v-icon class="mb-2" color="grey lighten-1" large>
                mdi-filter-off-outline
              </v-icon>
              <span v-if="hasModuleFilters">No modules match the filters</span>
              <span v-else>No modules found</span>
            </div>
            <v-card
              v-for="module in filteredModules"
              :key="module.id"
              :class="{ focused: focusedDrawingId === module.id }"
              class="mb-2"
              outlined
            >
              <v-list-item
                :value="module.id"
                class="px-4"
                color="primary"
                @click="
                  displayDrawing(
                    projectModulesTableId,
                    module.id,
                    module['2d_drawing']
                  )
                "
              >
                <v-list-item-icon class="my-auto mr-3">
                  <v-icon
                    :color="module['2d_drawing'] ? 'primary' : 'grey lighten-1'"
                  >
                    {{
                      module['2d_drawing']
                        ? 'mdi-image'
                        : 'mdi-image-off-outline'
                    }}
                  </v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title class="d-flex align-center">
                    {{ module.module_id }}
                    <v-chip
                      v-if="module.module_type"
                      class="ml-2"
                      label
                      x-small
                    >
                      {{ module.module_type }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="d-flex align-center mt-1">
                    <v-icon class="mr-1" x-small>mdi-home-outline</v-icon>
                    {{ module.build_nr }}
                    <v-icon class="ml-3 mr-1" x-small>
                      mdi-order-numeric-ascending
                    </v-icon>
                    {{ module.assembly_sequence }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <!-- Assembly planning status -->
                <v-chip
                  v-if="moduleStatusMap[module.module_id]"
                  :color="
                    moduleStatusColor(moduleStatusMap[module.module_id].status)
                  "
                  class="flex-shrink-0"
                  outlined
                  small
                >
                  <v-icon left small>{{
                    moduleStatusIcon(moduleStatusMap[module.module_id].status)
                  }}</v-icon>
                  {{
                    moduleStatusLabel(
                      moduleStatusMap[module.module_id].status,
                      moduleStatusMap[module.module_id].locationName
                    )
                  }}
                </v-chip>
                <v-progress-circular
                  v-else-if="assemblyTasksLoading"
                  color="grey"
                  indeterminate
                  size="14"
                  width="2"
                />
              </v-list-item>
            </v-card>
          </v-list-item-group>
        </v-list>

        <!-- Trestles tab: left panel shows nothing extra -->
        <div
          v-else
          class="flex-grow-1 d-flex flex-column align-center justify-center pa-4 text--secondary"
        >
          <v-icon class="mb-2" color="grey lighten-1" large>
            mdi-arrow-right-thin
          </v-icon>
          <span>Use the controls on the right to navigate trestles.</span>
        </div>
      </div>

      <!-- No project selected -->
      <div
        v-else
        class="flex-grow-1 d-flex flex-column align-center justify-center pa-4 text--secondary"
      >
        <v-icon class="mb-2" color="grey lighten-1" large> mdi-factory </v-icon>
        <span>Select a hall and project to get started</span>
      </div>
    </div>

    <!-- Right panel -->
    <div class="flex-grow-1 d-flex flex-column overflow-hidden">
      <!-- PDF viewer (overview / modules tabs) -->
      <template v-if="drawingTab !== 'trestles'">
        <v-progress-linear
          v-show="imageLoading"
          color="primary"
          indeterminate
        />
        <div class="flex-grow-1 d-flex align-center justify-center">
          <iframe
            v-if="displayedDrawing"
            :src="displayedDrawing"
            height="100%"
            type="application/pdf"
            width="100%"
          />
          <div v-else class="d-flex flex-column align-center text--secondary">
            <v-icon class="mb-2" color="grey lighten-1" large>
              mdi-image-off-outline
            </v-icon>
            <span>{{
              imageLoading
                ? 'Loading drawing...'
                : 'Select an item to view its drawing'
            }}</span>
          </div>
        </div>
      </template>

      <!-- Trestle view -->
      <template v-else>
        <!-- Bounded container so the trestle overview scrolls per column
             (toolbar stays fixed), matching the Assembly Hall Dashboard. -->
        <div
          v-if="focusedHall && focusedProject"
          class="d-flex flex-grow-1 overflow-hidden"
        >
          <automated-trestle-overview
            v-if="focusedHall.automated"
            :selected-project="focusedProject"
          />
          <manual-trestle-overview v-else :selected-project="focusedProject" />
        </div>
        <div v-else class="flex-grow-1 d-flex align-center justify-center">
          <span>Select a hall and project to view trestles.</span>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.focused {
  border: solid 1px var(--v-primary-base);
}
</style>
